import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { PrimaryButton } from "../UI";
import TableShimmer from '../UI/Shimmer/TableShimmer';
import AgGridWrapper, { getAgGridColumnVisibilityStorageKey } from '../UI/AgGrid';
import InlineTooltip from '../UI/Tooltip/InlineTooltip';
import DateRangePicker from '../UI/DateRangePicker';
import { CSVLink } from "react-csv";
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { getAdobeTargetUsage, getAdobeAnalyticsScores, updateAdobeAnalyticsScores, getAdobeAnalyticsDashboardCards, saveAdobeAnalyticsDashboardCards } from '../../utils/APIs';
import dayjs from 'dayjs';
import ManageDashboardCardsModal from './ManageDashboardCardsModal';

// Human-readable headers per event_filter key
const EF_HEADERS = {
    create: 'Activity Created',
    update: 'Activity Updated',
    patch: 'Activity Patched',
};

const USAGE_DETAIL_COLS = [
    { field: 'create',              label: 'Activity Created' },
    { field: 'update',              label: 'Activity Updated' },
    { field: 'patch',               label: 'Activity Patched' },
    { field: 'count',               label: 'Total Score' },
];

// Expandable sub-row: shows per-user breakdown for project/metadata_key cards
const FullWidthDetailRenderer = ({ data, eventFilterHeaders }) => {
    const ef = data.event_filters || [];
    const headers = eventFilterHeaders || EF_HEADERS;

    // const isUsage = data.cardType === 'usage';
    // const countLabel = data.cardType === 'dashboard' ? 'Views' : 'Total Logins';
    const hidden = data.hiddenColumns || new Set();
    // const visibleUsageCols = USAGE_DETAIL_COLS.filter(col => !hidden.has(col.field));

    return (
        <div className="px-10 py-3 bg-gray-50 border-b border-gray-200 h-full overflow-y-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="pb-2 font-medium pr-8">User</th>

                        {ef.map(k => <th key={k} className="pb-2 font-medium pr-6">{headers[k] || k}</th>)}
                        <th className="pb-2 font-medium">Total</th>
                        
                        {/* {isUsage ? (
                            visibleUsageCols.map(col => (
                                <th key={col.field} className="pb-2 font-medium pr-6">{col.label}</th>
                            ))
                        ) : (
                            !hidden.has('count') && <th className="pb-2 font-medium">{countLabel}</th>
                        )} */}
                    </tr>
                </thead>
                <tbody>
                    {(data.users || []).map((user, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 pr-8 text-gray-800">{user.name}</td>

                            {ef.map(k => <td key={k} className="py-2 pr-6 text-gray-600">{(user[k] ?? 0).toLocaleString()}</td>)}
                            <td className="py-2 text-gray-700 font-medium">{(user.count ?? 0).toLocaleString()}</td>
                            
                            {/* {isUsage ? (
                                visibleUsageCols.map(col => (
                                    <td
                                        key={col.field}
                                        className={`py-2 pr-6 ${col.field === 'count' ? 'text-gray-700 font-medium' : 'text-gray-600'}`}
                                    >
                                        {(user[col.field] ?? 0).toLocaleString()}
                                    </td>
                                ))
                            ) : (
                                !hidden.has('count') && <td className="py-2 text-gray-600">{(user.count ?? 0).toLocaleString()}</td>
                            )} */}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const AdobeTargetUsage = ({ setShowBackNavigationArrow }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Default fallback in case Contentful is missing the configuration
    const UI_CONTENT = content?.ADOBE_TARGET_USAGE_CONTENT || {
        "title": "Adobe Target Usage",
        "description": "View and analyze Adobe Target usage across your organization.",
        "start_date_label": "Start Date",
        "end_date_label": "End Date",
        "submit_btn_text": "Submit",
        "fetching_btn_text": "Fetching...",
        "no_data_msg": "No usage data found for the selected date range.",
        "no_data_available": "No data available.",
        "back_btn_text": "Back to Dashboard",
        "download_csv_btn": "Download CSV",
        "search_placeholder": "Search...",
        "total_records_text": "total records",
        "view_details_btn": "View Details →",
        "detail_table_headers": {
            "index": "#",
            "name": "Name",
            "count": "Count",
            "total": "Total"
        },
        "card_colors": ["#F5A623", "#4A90D9", "#7B61FF", "#E74C3C", "#2ECC71", "#9B59B6"],
        "event_filter_headers": {
            "create": "Activity Created",
            "update": "Activity Updated",
            "patch": "Activity Patched"
        },
        "edit_scores_btn_text": "Edit Usage Scores",
        "manage_cards_tooltip": "Manage Dashboard Cards",
        "scores_modal": {
            "title": "Configure Event Scores",
            "description": "Set the score weight for each Adobe Target event. Changes apply retroactively to all usage data.",
            "event_col_header": "Event",
            "score_col_header": "Score",
            "cancel_btn": "Cancel",
            "save_btn": "Save",
            "saving_btn": "Saving...",
            "loading": "Loading...",
            "error_load": "Failed to load event scores.",
            "error_save": "Failed to save event scores.",
            "success_save": "Scores saved successfully."
        }
    };

    const [dashboardCards, setDashboardCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(true);
    const [showManageCardsModal, setShowManageCardsModal] = useState(false);

    const [cardsData, setCardsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [showScoresModal, setShowScoresModal] = useState(false);
    const [scores, setScores] = useState([]);
    const [scoreConfig, setScoreConfig] = useState({ loading: false, saving: false, error: '', saved: false });

    const canEditScores = userData?.selectedOrganization?.role?.some(
        r => r.name.toLowerCase().includes('admin') || r.name.toLowerCase().includes('owner')
    );

    const activeLabel = searchParams.get('label');
    // Only show visible cards on the dashboard
    const ALL_CARDS = dashboardCards.filter(c => c.is_visible !== false);

    const activeConfig = ALL_CARDS.find((e) => e.label === activeLabel);
    const isMetadataCard = activeConfig?.group_by === 'metadata_key';
    const isExpandableCard = ['project', 'metadata_key'].includes(activeConfig?.group_by);

    const [expandedRows, setExpandedRows] = useState(new Set());

    const [gridWidth, setGridWidth] = useState('100%');
    const resizeObserverRef = useRef(null);

    const gridContainerRef = useCallback(node => {
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
        }
        if (node) {
            resizeObserverRef.current = new ResizeObserver(entries => {
                for (let entry of entries) {
                    setGridWidth(entry.contentRect.width);
                }
            });
            resizeObserverRef.current.observe(node);
        }
    }, []);

    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const columnPickerRef = useRef(null);

    const columnVisibilityKey = getAgGridColumnVisibilityStorageKey(
        `adobe-target-usage-detail-table_${activeLabel}`
    );

    useEffect(() => {
        setExpandedRows(new Set());
    }, [activeLabel]);

    useEffect(() => {
        const fetchCards = async () => {
            if (!userData?.token || !userData?.selectedOrganization?.org_token) return;
            try {
                setLoadingCards(true);
                const response = await getAdobeAnalyticsDashboardCards(userData.token, userData.selectedOrganization.org_token, 'adobe_target');
                const cards = response?.data || [];
                setDashboardCards(cards);
            } catch (err) {
                console.error("Failed to fetch dashboard cards:", err);
            } finally {
                setLoadingCards(false);
            }
        };
        fetchCards();
    }, [userData?.token, userData?.selectedOrganization?.org_token]);

    useEffect(() => {
        if (!activeLabel) return;
        try {
            const saved = JSON.parse(localStorage.getItem(columnVisibilityKey));
            setHiddenColumns(saved?.length ? new Set(saved) : new Set());
        } catch {
            setHiddenColumns(new Set());
        }
        setShowColumnPicker(false);
    }, [activeLabel, columnVisibilityKey]);

    useEffect(() => {
        if (!activeLabel) return;
        try {
            localStorage.setItem(columnVisibilityKey, JSON.stringify([...hiddenColumns]));
        } catch {}
    }, [hiddenColumns, activeLabel, columnVisibilityKey]);

    useEffect(() => {
        const handler = (e) => {
            if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
                setShowColumnPicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggleRow = useCallback((name) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
        setTimeout(() => {
            if (gridApi) {
                gridApi.resetRowHeights();
            }
        }, 0);
    }, [gridApi]);

    const toggleColumn = useCallback((field) => {
        setHiddenColumns(prev => {
            const next = new Set(prev);
            if (next.has(field)) next.delete(field);
            else next.add(field);
            return next;
        });
    }, []);
    
    // Hide global back arrow when in detail mode
    useEffect(() => {
        if (setShowBackNavigationArrow) {
            setShowBackNavigationArrow(!activeLabel);
        }
        // Cleanup when component unmounts
        return () => {
            if (setShowBackNavigationArrow) setShowBackNavigationArrow(true);
        };
    }, [activeLabel, setShowBackNavigationArrow]);

    const activeCardData = cardsData.find((c) => c.label === activeLabel);
    const detailData = activeCardData?.data || [];
    const getRowHeight = useCallback((params) => {
        if (expandedRows.has(params.data?.name)) {
            return Math.min((params.data.users?.length || 0) * 36 + 60, 480) + 48; // 48 is base row height
        }
        return 48;
    }, [expandedRows]);

    const pinnedTotalsRow = useMemo(() => {
        if (!detailData.length) return null;

        const baseRows = detailData.filter(row => !row._isDetail);

        const totals = { name: UI_CONTENT.detail_table_headers?.total || 'Total' };

        const numericKeys = Object.keys(baseRows[0] || {}).filter(
            key => typeof baseRows[0]?.[key] === 'number'
        );

        numericKeys.forEach(key => {
            totals[key] = baseRows.reduce(
                (sum, row) => sum + (row[key] || 0),
                0
            );
        });

        return totals;
    }, [detailData, UI_CONTENT]);

    const columns = useMemo(() => {
        const activeCardConfig = ALL_CARDS.find(c => c.label === activeLabel);
        const ef = activeCardConfig?.event_filters || [];

        const indexCol = {
            headerName: UI_CONTENT.detail_table_headers?.index || '#',
            width: 60,
            sortable: false,
            filter: false,
            cellStyle: (params) => {
                if (expandedRows.has(params.data?.name)) {
                    return { overflow: 'visible', zIndex: 999 };
                }
                return {};
            },
            cellRenderer: (params) => {
                const isExpanded = expandedRows.has(params.data?.name);
                const value = params.node.rowPinned || params.data?._isDetail ? '' : params.node.rowIndex + 1;
                
                return (
                    <div className="h-full w-full relative">
                        <div className="flex items-center h-[48px]">
                            {value}
                        </div>
                        {isExpanded && (
                            <div 
                                className="absolute top-[48px] bg-white shadow-lg border border-gray-200 rounded-b-lg"
                                style={{ left: '-17px', width: gridWidth, height: 'calc(100% - 48px)', overflowY: 'auto' }}
                            >
                                <FullWidthDetailRenderer 
                                    data={{ ...params.data, cardType: params.data.cardType, hiddenColumns, event_filters: activeConfig?.event_filters || [] }} 
                                    eventFilterHeaders={UI_CONTENT.event_filter_headers || EF_HEADERS} 
                                />
                            </div>
                        )}
                    </div>
                );
            }
        };
        const countCol = {
            headerName: activeCardConfig?.count_header || UI_CONTENT.detail_table_headers?.count || 'Count',
            field: 'count',
            sortable: true,
            filter: true,
            resizable: true,
            width: 130
        };
        const nameCol = isExpandableCard
            ? {
                headerName: UI_CONTENT.detail_table_headers?.name || 'Name',
                field: 'name',
                sortable: true,
                filter: true,
                resizable: true,
                flex: 1,
                minWidth: 200,
                cellStyle: (params) => {
                    // Keep overflow visible just in case, though detail is now in indexCol
                    if (expandedRows.has(params.data?.name)) {
                        return { overflow: 'visible' };
                    }
                    return {};
                },
                cellRenderer: (params) => {
                    const hasUsers = params.data?.users?.length > 0;
                    const isExpanded = expandedRows.has(params.data?.name);
                    const handleClick = hasUsers ? () => {
                        toggleRow(params.data?.name);
                    } : undefined;
                    
                    return (
                        <div className="h-full w-full relative">
                            <div className={`flex items-center gap-2 h-[48px] ${hasUsers ? 'cursor-pointer text-primary hover:underline' : ''}`} onClick={handleClick}>
                                {hasUsers && (
                                    <svg className={`w-4 h-4 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                )}
                                <span>{params.data?.name}</span>
                            </div>
                        </div>
                    );
                }
            }
            : {
                headerName: UI_CONTENT.detail_table_headers?.name || 'Name',
                field: 'name',
                sortable: true,
                filter: true,
                resizable: true,
                flex: 1,
                minWidth: 200
            };

        // Build columns dynamically from the card's event_filters
        const headers = UI_CONTENT.event_filter_headers || EF_HEADERS;
        const efCols = ef.map(k => ({
            headerName: headers[k] || k,
            field: k,
            sortable: true,
            filter: true,
            resizable: true,
            width: 145
        }));

        if (ef.length === 0) return [indexCol, nameCol, countCol];
        return [indexCol, nameCol, ...efCols, countCol];
    }, [activeLabel, ALL_CARDS, isExpandableCard, expandedRows, toggleRow, UI_CONTENT]);

    const visibleColumns = useMemo(
        () => columns.filter(col => !col.field || !hiddenColumns.has(col.field)),
        [columns, hiddenColumns]
    );

    const autoSizeStrategy = useMemo(() => ({
        type: window.innerWidth < 1024 ? 'fitCellContents' : 'fitGridWidth',
        defaultMinWidth: 100,
    }), []);

    // Refresh data when global Adobe IO integration changes, or on initial load
    useEffect(() => {
        if (startDate && endDate && userData?.defaultAdobeIO?.tenant_name) {
            handleSubmit(startDate, endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.defaultAdobeIO?.tenant_name]);

    // Fetch cards data (top 10)
    const handleSubmit = async (overrideStart, overrideEnd) => {
        const fetchStart = overrideStart || startDate;
        const fetchEnd = overrideEnd || endDate;
        if (!fetchStart || !fetchEnd) return;

        // Clear detail view if active
        if (activeLabel) {
            searchParams.delete('label');
            setSearchParams(searchParams, { state: location.state });
        }

        setHasSubmitted(true);
        setLoading(true);
        setError('');
        setCardsData([]);

        const tenant_name = userData?.defaultAdobeIO?.tenant_name;
        const formattedStart = `${fetchStart}T00:00:00-07`;
        const formattedEnd = `${fetchEnd}T00:00:00-07`;

        try {
            const result = await getAdobeTargetUsage(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                tenant_name,
                formattedStart,
                formattedEnd,
                [], // Backend will fetch directly from DB now
                false,
                userData?.defaultAdobeIO?._id,
                'adobe_target'
            );
            setCardsData(result?.data || []);
        } catch (err) {
            console.log('Error fetching Adobe Target usage:', err);
            setError(err?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Just update URL query param to activate detail view
    const handleViewDetails = (label) => {
        searchParams.set('label', label);
        setSearchParams(searchParams, { state: location.state });
        setSearchText('');
    };

    // Go back to cards view
    const handleBackToCards = () => {
        searchParams.delete('label');
        setSearchParams(searchParams, { state: location.state });
        setSearchText('');
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        gridApi?.setQuickFilter(e.target.value);
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const handleOpenScoresModal = async () => {
        setShowScoresModal(true);
        setScoreConfig({ loading: true, saving: false, error: '', saved: false });
        try {
            const result = await getAdobeAnalyticsScores(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                'adobe_target'
            );
            setScores(result?.data || []);
        } catch (err) {
            setScoreConfig(prev => ({ ...prev, error: err?.message || UI_CONTENT.scores_modal?.error_load || 'Failed to load event scores.' }));
        } finally {
            setScoreConfig(prev => ({ ...prev, loading: false }));
        }
    };

    const handleSaveScores = async () => {
        setScoreConfig(prev => ({ ...prev, saving: true, error: '', saved: false }));
        try {
            await updateAdobeAnalyticsScores(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                scores.map(s => ({ event_key: s.event_key, score: Number(s.score) })),
                'adobe_target'
            );
            setShowScoresModal(false);
            if (hasSubmitted && startDate && endDate) {
                handleSubmit(startDate, endDate);
            }
        } catch (err) {
            setScoreConfig(prev => ({ ...prev, error: err?.message || UI_CONTENT.scores_modal?.error_save || 'Failed to save event scores.' }));
        } finally {
            setScoreConfig(prev => ({ ...prev, saving: false }));
        }
    };

    return (
        <>
            {!activeLabel && (
                <>
                    <div className="flex justify-between items-center w-full">
                        <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                            {UI_CONTENT.title}
                        </h1>
                        {canEditScores && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleOpenScoresModal}
                                    className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded hover:bg-primary hover:text-white transition-colors"
                                >
                                    {UI_CONTENT.edit_scores_btn_text || 'Edit Usage Scores'}
                                </button>
                                <button
                                    onClick={() => setShowManageCardsModal(true)}
                                    className="p-1.5 text-gray-500 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                                    title={UI_CONTENT.manage_cards_tooltip || "Manage Dashboard Cards"}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col pb-6 w-full mt-2">
                        <p>{UI_CONTENT.description}</p>
                        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end mt-6 ml-auto w-full sm:w-auto">
                            <DateRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onDateChange={(start, end) => {
                                    setStartDate(start);
                                    setEndDate(end);
                                    handleSubmit(start, end);
                                }}
                                label={`${UI_CONTENT.start_date_label} & ${UI_CONTENT.end_date_label}`}
                                allowFuture={false}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Error */}
            {error && <h3 className="text-center text-red-500 w-full">{error}</h3>}

            {/* ========== DETAIL VIEW (AgGrid table) ========== */}
            {activeLabel && !loading && (
                <div className="w-full">
                    {/* Top action row */}
                    <div className="flex w-full mb-3">
                        <button
                            onClick={handleBackToCards}
                            className="font-medium text-primary hover:underline flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5H1m0 0 4 4M1 5l4-4" />
                            </svg>
                            {UI_CONTENT.back_btn_text}
                        </button>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                            {activeConfig?.label || activeLabel}
                        </h1>

                        <div className="flex items-center gap-3">
                            {detailData.length > 0 && (
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={handleSearch}
                                    className="w-full md:min-w-56 md:w-auto p-2.5 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-200 appearance-none focus:outline-none"
                                    placeholder={UI_CONTENT.search_placeholder}
                                />
                            )}

                            {detailData.length > 0 && (
                                <div ref={columnPickerRef} className="relative">
                                    <button
                                        onClick={() => setShowColumnPicker(p => !p)}
                                        className="px-3 py-2.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                                        </svg>
                                        Customize
                                    </button>
                                    {showColumnPicker && (
                                        <div className="absolute right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px] max-h-80 overflow-y-auto">
                                            {columns
                                                .filter(col => col.field && col.field !== 'name')
                                                .map(col => (
                                                    <label key={col.field} className="flex items-center gap-2 py-1.5 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                                                        <input
                                                            type="checkbox"
                                                            checked={!hiddenColumns.has(col.field)}
                                                            onChange={() => toggleColumn(col.field)}
                                                            className="w-4 h-4 accent-primary"
                                                        />
                                                        {col.headerName}
                                                    </label>
                                                ))
                                            }
                                        </div>
                                    )}
                                </div>
                            )}

                            {detailData.length > 0 && (
                                <CSVLink
                                    data={detailData}
                                    filename={`adobe-target-usage-${(activeConfig?.label || activeLabel).replace(/\s+/g, '-').toLowerCase()}-${new Date().getTime()}.csv`}
                                >
                                    <PrimaryButton className="whitespace-nowrap w-full md:w-auto">
                                        {UI_CONTENT.download_csv_btn || "Download CSV"}
                                        <svg className="w-2.5 h-2.5 ms-3 ml-2 inline-block" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" />
                                        </svg>
                                    </PrimaryButton>
                                </CSVLink>
                            )}
                        </div>
                    </div>

                    <div ref={gridContainerRef} className="ag-theme-quartz" style={{ width: '100%' }}>
                        {detailData.length === 0 && (
                            <h3 className="text-center">{UI_CONTENT.no_data_available}</h3>
                        )}
                        {detailData.length > 0 && (
                            <AgGridWrapper
                                tableId="adobe-target-usage-detail-table"
                                rowData={detailData}
                                columnDefs={visibleColumns}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                }}
                                autoSizeStrategy={autoSizeStrategy}
                                onGridReady={onGridReady}
                                pinnedBottomRowData={pinnedTotalsRow ? [pinnedTotalsRow] : []}
                                getRowHeight={isExpandableCard ? getRowHeight : undefined}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* ========== CARDS VIEW ========== */}
            {!activeLabel && (
                <>
                    {/* Loading shimmer */}
                    {(loading || loadingCards) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mx-auto">
                            {(dashboardCards.length > 0 ? dashboardCards : [{ width: 'half' }, { width: 'half' }, { width: 'full' }, { width: 'full' }]).map((config, idx) => {
                                const colSpanClass = config.width === 'full' || config.width === '100%' ? 'md:col-span-2' : 'md:col-span-1';
                                return (
                                    <div key={idx} className={`border border-gray-200 rounded-lg p-5 animate-pulse ${colSpanClass}`}>
                                        <div className="h-5 bg-gray-200 rounded w-2/3 mb-4"></div>
                                        {Array.from({ length: 5 }).map((__, i) => (
                                            <div key={i} className="flex items-center gap-3 mb-3">
                                                <div className="h-4 bg-gray-200 rounded w-6"></div>
                                                <div className="h-4 bg-gray-200 rounded flex-1"></div>
                                                <div className="h-4 bg-gray-200 rounded w-10"></div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* No data */}
                    {!(loading || loadingCards) && !error && hasSubmitted && cardsData.length === 0 && (
                        <h3 className="text-center w-full">{UI_CONTENT.no_data_msg}</h3>
                    )}

                    {/* Cards grid */}
                    {!(loading || loadingCards) && !error && cardsData.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mx-auto">
                            {[...ALL_CARDS].sort((a, b) => a.sequence - b.sequence).map((config, cardIdx) => {
                                const card = cardsData.find((c) => c.label === config.label);
                                if (!card) return null;
                                // Use summary (single total row) for dashboard display when configured, otherwise use data rows
                                const items = config.display_mode === 'summary' ? (card?.summary || []) : (card?.data || []);
                                const maxValue = items.length > 0
                                    ? Math.max(...items.map((item) => item.count || 0), 1)
                                    : 1;
                                const colorArray = UI_CONTENT.card_colors || ['#4A90D9'];
                                const DEFAULT_THEME_COLOR = '#465DAE';
                                const bulletColor = config.color || colorArray[cardIdx % colorArray.length] || DEFAULT_THEME_COLOR;
                                const colSpanClass = config.width === 'full' || config.width === '100%' ? 'md:col-span-2' : 'md:col-span-1';

                                return (
                                    <div
                                        key={config.label}
                                        className={`border border-gray-200 rounded-lg p-5 bg-white ${colSpanClass}`}
                                    >
                                        {/* Card header */}
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="inline-block w-3 h-3 rounded-full shrink-0"
                                                    style={{ backgroundColor: bulletColor }}
                                                ></span>
                                                <div className="flex items-start gap-1 ">
                                                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                                                        {config.label}
                                                    </h3>
                                                    {config.help_text && <InlineTooltip content={config.help_text} />}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleViewDetails(config.label)}
                                                className="text-xs text-primary hover:underline font-medium"
                                            >
                                                {UI_CONTENT.view_details_btn}
                                            </button>
                                        </div>

                                        {/* Total records */}
                                        {items.length > 0 && (
                                            <div className="flex justify-between items-center mb-3">
                                                <p className="text-xs text-gray-400 ml-5">
                                                    {items.length.toLocaleString()} {config.total_records_text || UI_CONTENT.total_records_text}
                                                </p>
                                                {config.total_count_header && (
                                                    <p className="text-xs text-gray-500 font-medium mr-1">
                                                        {config.total_count_header} - {items.reduce((sum, item) => sum + (item.count || 0), 0).toLocaleString()}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Items list */}
                                        {items.length === 0 ? (
                                            <p className="text-sm text-gray-400">{UI_CONTENT.no_data_available}</p>
                                        ) : (
                                            <div className="flex flex-col gap-2">
                                                {items.slice(0, 5).map((item, idx) => {
                                                    const value = item.count || 1;
                                                    const barWidth = Math.max((value / maxValue) * 100, 5);

                                                    return (
                                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                                            <span className="text-gray-500 w-5 text-right shrink-0">
                                                                {idx + 1}.
                                                            </span>
                                                            <div className="flex-1 min-w-0 relative">
                                                                <div
                                                                    className="absolute inset-y-0 left-0 rounded"
                                                                    style={{
                                                                        width: `${item.count > 0 ? Math.max((item.count / maxValue) * 100, 5) : 0}%`,
                                                                        backgroundColor: `${bulletColor}22`,
                                                                    }}
                                                                ></div>
                                                                <span className="relative z-10 truncate block px-1 py-0.5 text-gray-800">
                                                                    {item.name}
                                                                </span>
                                                            </div>
                                                            <span className="text-gray-600 font-medium shrink-0 tabular-nums">
                                                                {(item.count ?? 0).toLocaleString()}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
            {showScoresModal && (
                <PrimaryModal
                    title={UI_CONTENT.scores_modal?.title || "Configure Event Scores"}
                    hideModal={setShowScoresModal}
                    widthClass="max-w-lg"
                >
                    <div className="px-6 pb-6">
                        {scoreConfig.loading ? (
                            <p className="text-sm text-gray-500 py-4 text-center">{UI_CONTENT.scores_modal?.loading || "Loading..."}</p>
                        ) : (
                            <>
                                <p className="text-sm text-gray-500 mb-4">
                                    {UI_CONTENT.scores_modal?.description || "Set the score weight for each Adobe Target event. Changes apply retroactively to all usage data."}
                                </p>
                                <table className="w-full text-sm mb-4">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="pb-2 font-medium">{UI_CONTENT.scores_modal?.event_col_header || "Event"}</th>
                                            <th className="pb-2 font-medium w-24 text-right">{UI_CONTENT.scores_modal?.score_col_header || "Score"}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scores.map((s, i) => (
                                            <tr key={s.event_key} className="border-b last:border-0">
                                                <td className="py-2 text-gray-800">{s.label}</td>
                                                <td className="py-2 text-right">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        value={s.score}
                                                        onChange={e => {
                                                            const updated = [...scores];
                                                            updated[i] = { ...s, score: e.target.value };
                                                            setScores(updated);
                                                        }}
                                                        className="w-20 p-1.5 text-sm text-right border border-gray-200 rounded focus:outline-none focus:border-primary"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {scoreConfig.error && <p className="text-sm text-red-500 mb-3">{scoreConfig.error}</p>}
                                {scoreConfig.saved && <p className="text-sm text-green-600 mb-3">{UI_CONTENT.scores_modal?.success_save || "Scores saved successfully."}</p>}
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowScoresModal(false)}
                                        className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                                    >
                                        {UI_CONTENT.scores_modal?.cancel_btn || "Cancel"}
                                    </button>
                                    <PrimaryButton handleClick={handleSaveScores} disable={scoreConfig.saving}>
                                        {scoreConfig.saving ? (UI_CONTENT.scores_modal?.saving_btn || 'Saving...') : (UI_CONTENT.scores_modal?.save_btn || 'Save')}
                                    </PrimaryButton>
                                </div>
                            </>
                        )}
                    </div>
                </PrimaryModal>
            )}
            {/* Manage Cards Modal */}
            {showManageCardsModal && (
                <ManageDashboardCardsModal
                    closeManageCardsModal={() => setShowManageCardsModal(false)}
                    dashboardId="adobe_target"
                    onSaveSuccess={(savedCards) => {
                        // Apply cards directly from what the modal saved — no extra re-fetch
                        const applyAndRefresh = async () => {
                            try {
                                let cards = savedCards;
                                if (!cards || !Array.isArray(cards)) {
                                    const response = await getAdobeAnalyticsDashboardCards(userData.token, userData.selectedOrganization.org_token, 'adobe_target');
                                    cards = response?.data || [];
                                }
                                setDashboardCards(cards);
                                // Re-fetch analytics with updated card config
                                handleSubmit(startDate, endDate);
                            } catch (err) {
                                console.error('Failed to refresh dashboard:', err);
                            }
                        };
                        applyAndRefresh();
                    }}
                />
            )}
        </>
    );
};

export default AdobeTargetUsage;